/**
 * Bulk enrollment invites — shared URL per OS, capped by license seats.
 * Used by Client Admin and Platform Admin (pass tenantId for X-Tenant-Id).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Plus, Ban, Link2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';

type GroupOpt = { id: string; name: string };

type Props = {
  /** When set (Platform Admin), all calls include X-Tenant-Id */
  tenantId?: string;
  groups?: GroupOpt[];
};

const DESKTOP_PLATFORMS = ['windows', 'macos', 'linux'] as const;

function defaultMode(platform: string): string {
  if (platform === 'android') return 'WORK_PROFILE';
  if (platform === 'ios') return 'USER_ENROLLMENT';
  return 'AGENT_MANAGED';
}

function friendlyInviteError(msg: string): string {
  if (msg.startsWith('invite_exceeds_license_seats:')) {
    const rest = msg.split(':')[1] || '';
    return `Max uses exceeds remaining license seats (${rest}). Lower max uses or raise max_devices.`;
  }
  if (msg.startsWith('invite_no_license_seats:')) {
    return `No license seats left (${msg.split(':')[1] || 'full'}). Raise max_devices in Platform Admin.`;
  }
  return msg;
}

export default function BulkEnrollmentPanel({ tenantId, groups: groupsProp }: Props) {
  const toast = useToast();
  const [groups, setGroups] = useState<GroupOpt[]>(groupsProp || []);
  const [invites, setInvites] = useState<any[]>([]);
  const [capacity, setCapacity] = useState<{
    max_devices: number | null;
    devices_in_use: number;
    remaining_seats: number | null;
  }>({ max_devices: null, devices_in_use: 0, remaining_seats: null });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<any[]>([]);

  const remaining = capacity.remaining_seats;
  const defaultMaxUses = useMemo(() => {
    if (remaining == null) return 200;
    return Math.max(1, Math.min(200, remaining));
  }, [remaining]);

  const [form, setForm] = useState({
    platform: 'windows',
    enrollment_mode: 'AGENT_MANAGED',
    expires_in_hours: 168,
    max_uses: 200,
    group_id: '',
  });

  // Sync max_uses when capacity loads (don't fight user edits after they touch it)
  const [maxUsesTouched, setMaxUsesTouched] = useState(false);
  useEffect(() => {
    if (!maxUsesTouched) {
      setForm((f) => ({ ...f, max_uses: defaultMaxUses }));
    }
  }, [defaultMaxUses, maxUsesTouched]);

  useEffect(() => {
    if (groupsProp) setGroups(groupsProp);
  }, [groupsProp]);

  const reload = useCallback(async () => {
    try {
      const [inv, cap, g] = await Promise.all([
        apiService.tenantListInvites(tenantId),
        apiService.tenantEnrollmentCapacity(tenantId),
        groupsProp
          ? Promise.resolve(groupsProp)
          : apiService.tenantListGroups(tenantId).then((rows: any[]) =>
              (Array.isArray(rows) ? rows : []).map((r) => ({
                id: String(r.id),
                name: String(r.name || r.id),
              }))
            ).catch(() => [] as GroupOpt[]),
      ]);
      setInvites(Array.isArray(inv) ? inv : []);
      setCapacity(cap || { max_devices: null, devices_in_use: 0, remaining_seats: null });
      if (!groupsProp) setGroups(g);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load enrollment data');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast is stable enough; avoid reload loops
  }, [tenantId, groupsProp]);

  useEffect(() => {
    reload();
  }, [reload]);

  const clampedMaxUses = (n: number) => {
    let v = Math.max(1, Math.floor(n) || 1);
    if (remaining != null) v = Math.min(v, Math.max(1, remaining));
    return v;
  };

  const buildBody = (platform: string) => {
    const body: Record<string, unknown> = {
      platform,
      enrollment_mode: platform === form.platform ? form.enrollment_mode : defaultMode(platform),
      expires_in_hours: Math.max(1, form.expires_in_hours || 168),
      max_uses: clampedMaxUses(form.max_uses),
    };
    if (form.group_id) body.group_id = form.group_id;
    return body;
  };

  const createOne = async (platform: string) => {
    return apiService.tenantCreateEnrollmentInvite(buildBody(platform), tenantId);
  };

  const onCreate = async (platforms: string[]) => {
    if (remaining != null && remaining <= 0) {
      toast.error('No license seats remaining. Raise max_devices first.');
      return;
    }
    setBusy(true);
    const results: any[] = [];
    const errors: string[] = [];
    try {
      for (const p of platforms) {
        try {
          results.push(await createOne(p));
        } catch (e: any) {
          errors.push(`${p}: ${friendlyInviteError(e?.message || 'failed')}`);
        }
      }
      if (results.length) {
        setCreated(results);
        toast.success(
          results.length === 1
            ? 'Bulk enrollment link created'
            : `Created ${results.length} desktop enrollment links`
        );
        await reload();
      }
      if (errors.length) toast.error(errors.join(' · '));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (id: string) => {
    try {
      await apiService.tenantRevokeEnrollmentInvite(id, tenantId);
      toast.success('Invite revoked');
      await reload();
    } catch (e: any) {
      toast.error(friendlyInviteError(e?.message || 'Revoke failed'));
    }
  };

  const seatsLabel =
    remaining == null
      ? `Devices in use: ${capacity.devices_in_use} (unlimited license seats)`
      : `License seats: ${capacity.devices_in_use}/${capacity.max_devices} used · ${remaining} remaining`;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 flex items-start gap-2">
        <Link2 size={16} className="mt-0.5 shrink-0 text-indigo-600" />
        <div>
          <p className="font-medium">Bulk enrollment page</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Shared <code className="text-[11px]">/enroll/&#123;token&#125;</code> URL — every device that installs
            with this link joins <b>this tenant only</b>
            {form.group_id ? ' (and the selected group)' : ''}. Cap max uses by remaining license seats.
          </p>
          <p className="text-xs mt-1 font-medium text-slate-600">{seatsLabel}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        <label className="text-xs text-slate-500 block">
          Platform
          <select
            className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm"
            value={form.platform}
            onChange={(e) => {
              const platform = e.target.value;
              setForm({ ...form, platform, enrollment_mode: defaultMode(platform) });
            }}
          >
            <option value="windows">windows</option>
            <option value="macos">macos</option>
            <option value="linux">linux</option>
            <option value="android">android</option>
            <option value="ios">ios</option>
          </select>
        </label>
        <label className="text-xs text-slate-500 block">
          Enrollment mode
          <input
            className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm"
            value={form.enrollment_mode}
            onChange={(e) => setForm({ ...form, enrollment_mode: e.target.value })}
          />
        </label>
        <label className="text-xs text-slate-500 block">
          Max uses {remaining != null ? `(≤ ${remaining} seats)` : ''}
          <input
            type="number"
            min={1}
            max={remaining ?? 100000}
            className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm"
            value={form.max_uses}
            onChange={(e) => {
              setMaxUsesTouched(true);
              setForm({ ...form, max_uses: clampedMaxUses(Number(e.target.value)) });
            }}
          />
        </label>
        <label className="text-xs text-slate-500 block">
          Expires in hours
          <input
            type="number"
            min={1}
            className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm"
            value={form.expires_in_hours}
            onChange={(e) => setForm({ ...form, expires_in_hours: Math.max(1, Number(e.target.value) || 1) })}
          />
        </label>
        <label className="text-xs text-slate-500 block md:col-span-2">
          Group (optional — group-scoped link)
          <select
            className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm"
            value={form.group_id}
            onChange={(e) => setForm({ ...form, group_id: e.target.value })}
          >
            <option value="">Tenant-wide (no group)</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50"
          onClick={() => onCreate([form.platform])}
        >
          <Plus size={14} /> Create {form.platform} link
        </button>
        <button
          type="button"
          disabled={busy}
          className="px-3 py-1.5 bg-slate-800 text-white rounded-lg text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50"
          onClick={() => onCreate([...DESKTOP_PLATFORMS])}
          title="Creates three invites (one token per OS) with the same max uses, expiry, and group"
        >
          <Plus size={14} /> All desktop (Win + macOS + Linux)
        </button>
      </div>

      {created.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm space-y-3">
          <p className="font-semibold text-emerald-800">
            {created.length === 1 ? 'Invite created (token shown once)' : `${created.length} invites created (tokens shown once)`}
          </p>
          {created.map((inv) => (
            <div key={inv.id || inv.token} className="border-t border-emerald-100 pt-2 first:border-0 first:pt-0">
              <p className="text-xs font-medium text-emerald-900 uppercase">{inv.platform}</p>
              <p className="font-mono text-xs break-all mt-0.5">{inv.enrollment_url}</p>
              <p className="text-xs text-emerald-800 mt-0.5">
                Max uses: {inv.max_uses}
                {inv.expires_at ? ` · Expires ${new Date(inv.expires_at).toLocaleString()}` : ''}
                {inv.group_id ? ' · Group-scoped' : ''}
              </p>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-indigo-700 text-xs mt-1"
                onClick={() => {
                  navigator.clipboard.writeText(inv.enrollment_url);
                  toast.success('Invite URL copied', 'Copied');
                }}
              >
                <Copy size={12} /> Copy URL
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-auto max-h-[420px]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Platform</th>
              <th>Group</th>
              <th>Uses</th>
              <th>Status</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invites.map((i) => (
              <tr key={i.id} className="border-b border-slate-50">
                <td className="py-2">{i.platform}</td>
                <td className="text-xs">{i.group_name || (i.group_id ? String(i.group_id).slice(0, 8) : '—')}</td>
                <td>{i.use_count}/{i.max_uses}</td>
                <td>{i.status}</td>
                <td className="text-xs">{i.expires_at ? new Date(i.expires_at).toLocaleString() : '—'}</td>
                <td className="text-right">
                  {i.status === 'active' && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 text-xs text-rose-600 hover:underline"
                      onClick={() => onRevoke(i.id)}
                    >
                      <Ban size={12} /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {invites.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-slate-500 text-center">No invites yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
